import { Injectable, computed, signal, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Api } from './api';
import { environment } from '../../../environments/environment';
import { first, of, catchError } from 'rxjs';
import { SweetAlertUtil, UserUtil } from './util';
import { PusherBeamsService } from './pusher-beams';
import { NotificationService } from './notification';

type LoginRequest = { identity: string; password: string };

export interface AuthUser {
  id: any;
  f_name: string | null;
  l_name: string | null;
  title?: string | null;
  pf_no: string;
  email: string | null;
  phone: string | null;
  department?: string | null;
  dept_id?: string | number | null;
  dept_name?: string | null;
  designation?: string | null;
  designation_id?: string | number | null;
  user_image?: string | null;
  signature?: string | null;
  dob?: string | null;
  blood_group?: string | null;
  [key: string]: unknown;
}

export type Permission = string;

export interface LoginResponse {
  token: string;
  token_expires_at: string;
  user: AuthUser;
  permissions?: any[];
}

@Injectable({
  providedIn: 'root'
})
export class Auth {
  private readonly router = inject(Router);
  private readonly api = inject(Api);
  private readonly pusherBeams = inject(PusherBeamsService);
  private readonly notificationService = inject(NotificationService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly storageKeys = {
    token: 'auth.token',
    user: 'auth.user',
    permissions: 'auth.permissions',
    expiresAt: 'auth.expires_at'
  } as const;

  // Signals
  readonly token = signal<string | null>(null);
  readonly user = signal<AuthUser | null>(null);
  readonly permissions = signal<Permission[]>([]);
  readonly expiresAt = signal<string | null>(null);

  readonly isAuthenticated = computed(() => !!this.token());

  // Computed helper to check permissions
  hasPermission(permission: string): boolean {
    const perms = this.permissions();
    return perms.includes(permission);
  }

  // Check if user has ANY of the provided permissions
  hasAnyPermission(permissions: string[]): boolean {
    const currentPerms = this.permissions();
    return permissions.some(p => currentPerms.includes(p));
  }

  private isLoggingOut = false;

  // Called from APP_INITIALIZER
  init(): void {
    try {
      const token = localStorage.getItem(this.storageKeys.token);
      const userRaw = localStorage.getItem(this.storageKeys.user);
      const permsRaw = localStorage.getItem(this.storageKeys.permissions);
      const expiresAt = localStorage.getItem(this.storageKeys.expiresAt);

      if (token && expiresAt) {
        // Check if explicitly expired
        if (new Date(expiresAt) <= new Date()) {
          this.clearSession();
          return;
        }

        this.token.set(token);
        this.expiresAt.set(expiresAt);
        
        if (userRaw) {
          const user = JSON.parse(userRaw);
          this.sanitizeUser(user);
          this.user.set(user);

          // Start Pusher Beams if authenticated
          if (user.id && token) {
            this.pusherBeams.start(user.id, token).catch(err => {
              console.error('Failed to start Pusher Beams on init:', err);
            });
          }
        }

        if (permsRaw) {
          this.permissions.set(JSON.parse(permsRaw));
        }

        this.notificationService.requestPermission();
        this.scheduleAutoLogout(expiresAt);
      } else {
        this.clearSession();
      }
    } catch {
      this.clearSession();
    }
  }

  login(credentials: LoginRequest) {
    return this.api.login({ identity: credentials.identity, password: credentials.password });
  }

  // To be called by consumer after successful login() subscribe next
  establishSession(response: any): void {
    const data = response.data || response;
    const { token, user, permissions, token_expires_at } = data;

    if (token) {
      // Ensure we don't store 'Bearer ' in the signal/localStorage for consistency,
      // although the interceptor usually handles this, it's safer for other services.
      const cleanToken = token.replace(/^Bearer\s+/i, '').trim();
      this.token.set(cleanToken);
      localStorage.setItem(this.storageKeys.token, cleanToken);
    }

    if (token_expires_at) {
      this.expiresAt.set(token_expires_at);
      localStorage.setItem(this.storageKeys.expiresAt, token_expires_at);
      this.scheduleAutoLogout(token_expires_at);
    }

    if (user) {
      this.sanitizeUser(user);
      this.user.set(user);
      localStorage.setItem(this.storageKeys.user, JSON.stringify(user));
    }

    // Extract permission names from objects if they are objects
    const perms = (permissions ?? []).map((p: any) => typeof p === 'string' ? p : p.name);
    this.permissions.set(perms);
    localStorage.setItem(this.storageKeys.permissions, JSON.stringify(perms));

    // Start Pusher Beams
    if (user && user.id && token) {
      this.pusherBeams.start(user.id, token).catch(err => {
        console.error('Failed to start Pusher Beams:', err);
      });
    }
  }

  logout(force = false): void {
    if (this.isLoggingOut) return;
    this.isLoggingOut = true;

    // 1. Silent backend logout (notify backend)
    // We trigger this BEFORE clearing session so the interceptor has the token
    if (!force && this.token()) {
      this.api.logout().pipe(
        first(),
        catchError(() => of(null))
      ).subscribe();
    }

    // 2. Immediate local logout and navigation
    this.clearSession();
    this.router.navigateByUrl('/login');
    this.isLoggingOut = false;
  }

  private clearSession(): void {
    this.token.set(null);
    this.user.set(null);
    this.permissions.set([]);
    this.expiresAt.set(null);
    localStorage.removeItem(this.storageKeys.token);
    localStorage.removeItem(this.storageKeys.user);
    localStorage.removeItem(this.storageKeys.permissions);
    localStorage.removeItem(this.storageKeys.expiresAt);
    
    // Stop Pusher Beams
    this.pusherBeams.stop().catch(err => {
      console.error('Failed to stop Pusher Beams:', err);
    });

    if (this.autoLogoutTimer) {
      clearTimeout(this.autoLogoutTimer);
    }
  }

  private autoLogoutTimer?: any;
  private scheduleAutoLogout(expiresAt: string): void {
    if (this.autoLogoutTimer) {
      clearTimeout(this.autoLogoutTimer);
    }

    const expiryTime = new Date(expiresAt).getTime();
    const nowTime = new Date().getTime();
    const delay = expiryTime - nowTime;

    if (delay > 0) {
      this.autoLogoutTimer = setTimeout(() => {
        this.handleSessionExpired();
      }, delay);
    } else {
      this.handleSessionExpired();
    }
  }

  private handleSessionExpired(): void {
    if (!this.isAuthenticated()) return;

    SweetAlertUtil.error('Session expired. Please login again.', 'Session Expired');
    this.logout(true); // Force logout
  }

  private sanitizeUser(user: any): void {
    if (!user || typeof user !== 'object') return;
    Object.keys(user).forEach(key => {
      if (typeof user[key] === 'string') {
        user[key] = UserUtil.sanitize(user[key]);
      }
    });
  }
}
